import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getCompanySettings } from '@/utils/companySettings';
import { 
  ArrowLeft, FileText, Upload, Save, Eye, Download, Plus, Trash2, 
  FileSignature, Type, Hash, Calendar, DollarSign, Mail, Phone,
  MapPin, User, Building2, Layers, Settings, ChevronDown, ChevronUp,
  Loader2, CheckCircle2, X, Copy, RefreshCw, Image, FilePlus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';
import { openPdf } from '@/utils/openPdf';
import { BrandLockup } from '@/components/brand/Brand';

const API = process.env.REACT_APP_BACKEND_URL;

// Field types available for auto-fill
const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type, description: 'Single line text input' },
  { value: 'textarea', label: 'Multi-line Text', icon: FileText, description: 'Multi-line text area' },
  { value: 'number', label: 'Number', icon: Hash, description: 'Numeric value' },
  { value: 'currency', label: 'Currency', icon: DollarSign, description: 'Dollar amount' },
  { value: 'date', label: 'Date', icon: Calendar, description: 'Date picker' },
  { value: 'email', label: 'Email', icon: Mail, description: 'Email address' },
  { value: 'phone', label: 'Phone', icon: Phone, description: 'Phone number' },
  { value: 'address', label: 'Address', icon: MapPin, description: 'Street address' },
  { value: 'signature', label: 'Signature', icon: FileSignature, description: 'Signature field' },
];

// Auto-fill placeholders that can be used
const AUTO_FILL_PLACEHOLDERS = [
  { key: '{{client_name}}', label: 'Client Name', source: 'project' },
  { key: '{{client_email}}', label: 'Client Email', source: 'project' },
  { key: '{{client_phone}}', label: 'Client Phone', source: 'project' },
  { key: '{{project_name}}', label: 'Project Name', source: 'project' },
  { key: '{{project_code}}', label: 'Project Code', source: 'project' },
  { key: '{{project_address}}', label: 'Project Address', source: 'project' },
  { key: '{{designer_name}}', label: 'Designer Name', source: 'company' },
  { key: '{{designer_email}}', label: 'Designer Email', source: 'company' },
  { key: '{{company_name}}', label: 'Company Name', source: 'company' },
  { key: '{{company_phone}}', label: 'Company Phone', source: 'company' },
  { key: '{{company_address}}', label: 'Company Address', source: 'company' },
  { key: '{{today_date}}', label: 'Today\'s Date', source: 'system' },
  { key: '{{current_year}}', label: 'Current Year', source: 'system' },
  // DP64 Part 2a — resolves to company_settings.document_disclaimer, written on
  // Admin > Documents. The tenant decides which documents carry it and where.
  { key: '{{disclaimer}}', label: 'Disclaimer', source: 'company' },
];

// Document categories
const CATEGORIES = [
  { value: 'onboarding', label: 'Client Onboarding' },
  { value: 'pre_design', label: 'Pre-Design' },
  { value: 'schematic', label: 'Schematic Design' },
  { value: 'design_dev', label: 'Design Development' },
  { value: 'construction_docs', label: 'Construction Documents' },
  { value: 'contract', label: 'Contract & Bidding' },
  { value: 'construction_admin', label: 'Construction Admin' },
  { value: 'closeout', label: 'Project Closeout' },
  { value: 'general', label: 'General' },
];

export default function DocumentEditor() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const location = useLocation();
  const fileInputRef = useRef(null);

  // 2026-02-17 — Preserve originating context so Back / breadcrumb
  // return where the user came from. Two channels are supported:
  //   1. Route state:  navigate('/documents/editor/:id', { state: { from, fromLabel } })
  //   2. Query param:  /documents/editor/:id?from=%2Fprojects%2Fabc%2Fhub
  // Falls back to /documents when no origin was passed.
  const originHref = (() => {
    const stateFrom = location.state?.from;
    if (stateFrom && typeof stateFrom === 'string') return stateFrom;
    try {
      const q = new URLSearchParams(location.search).get('from');
      if (q) return decodeURIComponent(q);
    } catch { /* ignore malformed query */ }
    return null;
  })();
  const originLabel = location.state?.fromLabel || 'Back';
  const goBack = () => {
    if (originHref) navigate(originHref);
    else navigate('/documents');
  };
  
  // Template state
  const [template, setTemplate] = useState({
    name: '',
    description: '',
    category: 'general',
    content: '',
    fields: [],
    requires_signature: false,
    signature_fields: [],
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // DP64 — the tenant's own settings, for {{disclaimer}}.
  const [companySettings, setCompanySettings] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [activeTab, setActiveTab] = useState('edit');
  
  // New field form
  const [newField, setNewField] = useState({
    key: '',
    label: '',
    type: 'text',
    required: false,
    placeholder: '',
    description: '',
    default_value: '',
  });
  
  // Dark mode
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('keyMadeDarkMode');
    return saved === 'true';
  });

  useEffect(() => {
    getCompanySettings()
      .then((d) => setCompanySettings(d))
      .catch(() => setCompanySettings(null));
  }, []);

  useEffect(() => {
    const handleDarkModeChange = () => {
      const saved = localStorage.getItem('keyMadeDarkMode');
      setDarkMode(saved === 'true');
    };
    window.addEventListener('storage', handleDarkModeChange);
    const interval = setInterval(handleDarkModeChange, 2000);
    return () => {
      window.removeEventListener('storage', handleDarkModeChange);
      clearInterval(interval);
    };
  }, []);
  
  // Load existing template if editing
  useEffect(() => {
    if (templateId && templateId !== 'new') {
      loadTemplate();
    }
  }, [templateId]);
  
  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/document-templates/${templateId}`);
      const data = response.data;
      // Ensure fields array exists (map from variables if needed)
      setTemplate({
        ...data,
        fields: data.fields || data.variables || [],
        content: data.content || '',
        signature_fields: data.signature_fields || [],
      });
    } catch (error) {
      toast.error('Failed to load template');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF, Word document, or text file');
      return;
    }
    
    try {
      setLoading(true);
      
      // For text files, read the content directly
      if (file.type === 'text/plain') {
        const reader = new FileReader();
        reader.onload = (e) => {
          setTemplate(prev => ({
            ...prev,
            content: e.target.result,
            name: prev.name || file.name.replace(/\.[^/.]+$/, ''),
          }));
          setUploadedFile(file);
          toast.success('File loaded successfully');
        };
        reader.readAsText(file);
      } else {
        // For PDF/Word, upload to server for processing
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/api/document-templates/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setTemplate(prev => ({
          ...prev,
          content: response.data.content || '',
          file_url: response.data.file_url,
          name: prev.name || file.name.replace(/\.[^/.]+$/, ''),
        }));
        setUploadedFile(file);
        toast.success('File uploaded and processed');
      }
    } catch (error) {
      toast.error('Failed to process file');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  // Add a new field
  const addField = () => {
    if (!newField.key || !newField.label) {
      toast.error('Field key and label are required');
      return;
    }
    
    // Generate key from label if not provided
    const fieldKey = newField.key || newField.label.toLowerCase().replace(/\s+/g, '_');
    
    // Check for duplicate keys
    if (template.fields.some(f => f.key === fieldKey)) {
      toast.error('A field with this key already exists');
      return;
    }
    
    const field = {
      ...newField,
      key: fieldKey,
      id: Date.now().toString(),
    };
    
    setTemplate(prev => ({
      ...prev,
      fields: [...prev.fields, field],
    }));
    
    // Reset form
    setNewField({
      key: '',
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      description: '',
      default_value: '',
    });
    setShowFieldDialog(false);
    toast.success('Field added');
  };
  
  // Update a field
  const updateField = (index, updates) => {
    const updatedFields = [...template.fields];
    updatedFields[index] = { ...updatedFields[index], ...updates };
    setTemplate(prev => ({ ...prev, fields: updatedFields }));
  };
  
  // Remove a field
  const removeField = (index) => {
    const updatedFields = template.fields.filter((_, i) => i !== index);
    setTemplate(prev => ({ ...prev, fields: updatedFields }));
    toast.success('Field removed');
  };
  
  // Insert placeholder into content
  const insertPlaceholder = (placeholder) => {
    const textarea = document.getElementById('template-content');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = template.content;
      const before = text.substring(0, start);
      const after = text.substring(end);
      
      setTemplate(prev => ({
        ...prev,
        content: before + placeholder + after,
      }));
      
      // Set cursor position after placeholder
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
      }, 0);
    } else {
      // Just append if no cursor position
      setTemplate(prev => ({
        ...prev,
        content: prev.content + placeholder,
      }));
    }
  };
  
  // Save template
  const saveTemplate = async () => {
    if (!template.name) {
      toast.error('Template name is required');
      return;
    }
    
    try {
      setSaving(true);
      
      const payload = {
        name: template.name,
        description: template.description,
        category: template.category,
        content: template.content,
        fields: template.fields,
        requires_signature: template.requires_signature,
        signature_fields: template.signature_fields,
        file_url: template.file_url,
      };
      
      if (templateId && templateId !== 'new') {
        await axios.put(`${API}/api/document-templates/${templateId}`, payload);
        toast.success('Template updated');
      } else {
        const response = await axios.post(`${API}/api/document-templates/create`, payload);
        toast.success('Template created');
        navigate(`/documents/editor/${response.data.id}`);
      }
    } catch (error) {
      toast.error('Failed to save template');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };
  
  // Generate preview with sample data
  const generatePreview = () => {
    let previewContent = template.content;
    
    // Replace all placeholders with sample data
    AUTO_FILL_PLACEHOLDERS.forEach(p => {
      const sampleValue = getSampleValue(p.key);
      previewContent = previewContent.replace(new RegExp(p.key.replace(/[{}]/g, '\\$&'), 'g'), sampleValue);
    });
    
    // Replace field placeholders
    template.fields.forEach(field => {
      const placeholder = `{{${field.key}}}`;
      const sampleValue = field.default_value || `[${field.label}]`;
      previewContent = previewContent.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), sampleValue);
    });
    
    return previewContent;
  };
  
  const getSampleValue = (placeholder) => {
    const sampleData = {
      '{{client_name}}': 'Michael & Sarah Anderson',
      '{{client_email}}': 'anderson@example.com',
      '{{client_phone}}': '(417) 555-0123',
      '{{project_name}}': 'Anderson Home Renovation',
      '{{project_code}}': 'KC-MA20-2601',
      '{{project_address}}': '1234 Oak Street, Springfield, MO 65801',
      '{{designer_name}}': 'Chad Holgerson',
      '{{designer_email}}': 'chad@keystonecreative.design',
      '{{company_name}}': 'Keystone Creative',
      '{{company_phone}}': '(417) 555-0100',
      '{{company_address}}': '123 Design Avenue, Springfield, MO 65801',
      '{{today_date}}': new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      '{{current_year}}': new Date().getFullYear().toString(),
      // DP64 — not a sample. The tenant's real disclaimer, or blank if unset.
      '{{disclaimer}}': companySettings?.document_disclaimer || '',
    };
    return sampleData[placeholder] || placeholder;
  };
  
  // Dark mode classes
  const bgClass = darkMode ? 'bg-black' : 'bg-[#F8F7F5]';
  const headerClass = darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white border-border';
  const cardClass = darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white border-none';
  const inputClass = darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]' : '';
  const textClass = darkMode ? 'text-[#E5E2DD]' : '';
  
  if (loading && templateId !== 'new') {
    return (
      <div className={`min-h-screen ${bgClass} flex items-center justify-center`}>
        <Loader2 className="w-8 h-8 animate-spin text-[#BC5D41]" />
      </div>
    );
  }
  
  return (
    <div className={`min-h-screen ${bgClass}`}>
      {/* Header */}
      <header className={`${headerClass} border-b sticky top-0 z-50`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Breadcrumb */}
          <nav className={`flex items-center gap-2 text-sm mb-3 ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
            <button onClick={() => navigate('/dashboard')} className={`hover:underline ${darkMode ? 'hover:text-[#E5E2DD]' : ''}`}>
              My Desk
            </button>
            <span>/</span>
            {originHref ? (
              <button
                onClick={goBack}
                className={`hover:underline ${darkMode ? 'hover:text-[#E5E2DD]' : ''}`}
                data-testid="doc-editor-breadcrumb-origin"
              >
                {originLabel === 'Back' ? '← Back to project' : originLabel}
              </button>
            ) : (
              <button
                onClick={() => navigate('/documents')}
                className={`hover:underline ${darkMode ? 'hover:text-[#E5E2DD]' : ''}`}
              >
                Document Library
              </button>
            )}
            <span>/</span>
            <span className={textClass}>{templateId === 'new' ? 'New Template' : 'Edit Template'}</span>
          </nav>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={goBack}
                className={darkMode ? 'hover:bg-[#1A1A1A] text-[#8A8A8A]' : ''}
                data-testid="doc-editor-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {originHref ? (originLabel === 'Back' ? 'Back to project' : originLabel) : 'Back'}
              </Button>
              <div className={`h-6 w-px ${darkMode ? 'bg-[#2A2A2A]' : 'bg-border'}`} />
              <div className="flex items-center gap-2">
                <HelpIcon elementId="document-editor" position="inline" />
                <div>
                  <h1 className={`text-xl font-heading ${textClass}`}>
                    {templateId === 'new' ? 'Create Document Template' : 'Edit Document Template'}
                  </h1>
                  <span className={`flex items-baseline gap-1`}>
                    <BrandLockup onDark={darkMode} testId="doceditor-brand" />
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowPreview(true)} className={darkMode ? 'border-[#2A2A2A] hover:bg-[#1A1A1A]' : ''}>
                <Eye className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Preview
              </Button>
              <Button onClick={saveTemplate} disabled={saving} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />}
                Save Template
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-3 gap-6">
          {/* Left: Template Settings */}
          <div className="col-span-1 space-y-6">
            {/* Basic Info */}
            <Card className={`${cardClass} shadow-sm`}>
              <CardHeader>
                <CardTitle className={`text-base ${textClass}`}>Template Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className={textClass}>Template Name *</Label>
                  <Input
                    value={template.name}
                    onChange={(e) => setTemplate(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Design Agreement"
                    className={inputClass}
                  />
                </div>
                
                <div>
                  <Label className={textClass}>Description</Label>
                  <Textarea
                    value={template.description}
                    onChange={(e) => setTemplate(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of this template..."
                    rows={2}
                    className={inputClass}
                  />
                </div>
                
                <div>
                  <Label className={textClass}>Category</Label>
                  <Select 
                    value={template.category} 
                    onValueChange={(value) => setTemplate(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger className={inputClass}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value} className={darkMode ? 'text-[#E5E2DD] focus:bg-[#2A2A2A]' : ''}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label className={textClass}>Requires Signature</Label>
                    <p className={`text-xs ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
                      Client must sign this document
                    </p>
                  </div>
                  <Switch
                    checked={template.requires_signature}
                    onCheckedChange={(checked) => setTemplate(prev => ({ ...prev, requires_signature: checked }))}
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Upload Document */}
            <Card className={`${cardClass} shadow-sm`}>
              <CardHeader>
                <CardTitle className={`text-base ${textClass}`}>Upload Base Document</CardTitle>
                <CardDescription>
                  Upload an existing document to use as a starting point
                </CardDescription>
              </CardHeader>
              <CardContent>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    darkMode 
                      ? 'border-[#2A2A2A] hover:border-[#BC5D41] bg-[#1A1A1A]' 
                      : 'border-gray-300 hover:border-[#70170F] bg-gray-50'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className={`w-8 h-8 mx-auto mb-2 ${darkMode ? 'text-[#6B6B6B]' : 'text-gray-400'}`} strokeWidth={1.5} />
                  <p className={`text-sm ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>
                    Click to upload PDF, Word, or Text file
                  </p>
                  {uploadedFile && (
                    <Badge className="mt-2 bg-[#BC5D41]/10 text-[#BC5D41] border-0">
                      {uploadedFile.name}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Original File Viewer - Show if template has an uploaded file */}
            {template.file_path && template.original_filename && (
              <Card className={`${cardClass} shadow-sm border-2 ${darkMode ? 'border-[#BC5D41]/30' : 'border-[#70170F]/20'}`}>
                <CardHeader className="pb-2">
                  <CardTitle className={`text-base flex items-center gap-2 ${textClass}`}>
                    <FileText className="w-4 h-4 text-[#BC5D41]" />
                    Original File
                  </CardTitle>
                  <CardDescription>
                    Reference the original uploaded document
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-gray-50'}`}>
                    {template.file_type === 'pdf' ? (
                      <div className="w-10 h-10 rounded bg-red-100 dark:bg-red-900 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                        {template.original_filename}
                      </p>
                      <p className={`text-xs ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
                        {template.file_type?.toUpperCase()} • {template.file_size ? `${(template.file_size / 1024).toFixed(1)} KB` : ''}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className={`flex-1 ${darkMode ? 'border-[#2A2A2A] hover:bg-[#1A1A1A]' : ''}`}
                      onClick={() => openPdf(`${API}/api/document-templates/${templateId}/download`, `${template.name || 'template'}.${(template.file_type || 'pdf').toLowerCase()}`, { download: true, type: 'application/octet-stream' })}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className={`flex-1 ${darkMode ? 'border-[#2A2A2A] hover:bg-[#1A1A1A]' : ''}`}
                      onClick={() => openPdf(`${API}/api/document-templates/${templateId}/download`, `${template.name || 'template'}.${(template.file_type || 'pdf').toLowerCase()}`)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Button>
                  </div>
                  
                  <p className={`text-xs ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
                    💡 Open the original file to copy text and paste into the Content Editor
                  </p>
                </CardContent>
              </Card>
            )}
            
            {/* Auto-fill Placeholders */}
            <Card className={`${cardClass} shadow-sm`}>
              <CardHeader>
                <CardTitle className={`text-base ${textClass}`}>Auto-fill Placeholders</CardTitle>
                <CardDescription>
                  Click to insert into document content
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {AUTO_FILL_PLACEHOLDERS.map(p => (
                    <button
                      key={p.key}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        darkMode 
                          ? 'bg-[#1A1A1A] hover:bg-[#2A2A2A] text-[#E5E2DD]' 
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      onClick={() => insertPlaceholder(p.key)}
                    >
                      <span className="font-mono text-[#BC5D41]">{p.key}</span>
                      <span className={`ml-2 ${darkMode ? 'text-[#8A8A8A]' : 'text-muted-foreground'}`}>
                        {p.label}
                      </span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Right: Content Editor & Fields */}
          <div className="col-span-2 space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className={darkMode ? 'bg-[#1A1A1A]' : ''}>
                <TabsTrigger value="edit">Content Editor</TabsTrigger>
                <TabsTrigger value="fields">Custom Fields ({template.fields.length})</TabsTrigger>
              </TabsList>
              
              {/* Content Editor Tab */}
              <TabsContent value="edit">
                <Card className={`${cardClass} shadow-sm`}>
                  <CardHeader>
                    <CardTitle className={`text-base ${textClass}`}>Document Content</CardTitle>
                    <CardDescription>
                      Write or paste your document content. Use placeholders for auto-fill fields.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      id="template-content"
                      value={template.content}
                      onChange={(e) => setTemplate(prev => ({ ...prev, content: e.target.value }))}
                      placeholder={`Start writing your document content here...

Use placeholders like {{client_name}} or {{project_address}} which will be automatically filled when generating documents.

Example:

DESIGN AGREEMENT

This Agreement is entered into on {{today_date}} between:

Designer: {{company_name}}
         {{company_address}}

Client: {{client_name}}
        {{project_address}}

Project: {{project_name}} ({{project_code}})

...`}
                      rows={20}
                      className={`font-mono text-sm ${inputClass}`}
                    />
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Custom Fields Tab */}
              <TabsContent value="fields">
                <Card className={`${cardClass} shadow-sm`}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className={`text-base ${textClass}`}>Custom Fields</CardTitle>
                        <CardDescription>
                          Define fields that will be filled in when generating documents
                        </CardDescription>
                      </div>
                      <Button onClick={() => setShowFieldDialog(true)} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
                        <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Add Field
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {template.fields.length === 0 ? (
                      <div className={`text-center py-12 ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
                        <FilePlus className="w-12 h-12 mx-auto mb-4 opacity-50" strokeWidth={1} />
                        <p className={`text-lg font-medium mb-2 ${textClass}`}>No custom fields yet</p>
                        <p className="text-sm">
                          Add fields that users will fill in when creating documents from this template.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {template.fields.map((field, index) => {
                          const FieldIcon = FIELD_TYPES.find(t => t.value === field.type)?.icon || Type;
                          return (
                            <div 
                              key={field.id || index}
                              className={`flex items-center justify-between p-4 rounded-lg border ${
                                darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <div className="flex items-center gap-4">
                                <FieldIcon className={`w-5 h-5 ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`} strokeWidth={1.5} />
                                <div>
                                  <p className={`font-medium ${textClass}`}>{field.label}</p>
                                  <p className={`text-xs font-mono ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>
                                    {`{{${field.key}}}`}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className={darkMode ? 'bg-[#2A2A2A] text-[#8A8A8A]' : ''}>
                                    {field.type}
                                  </Badge>
                                  {field.required && (
                                    <Badge className="bg-[#BC5D41]/10 text-[#BC5D41] border-0">Required</Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => insertPlaceholder(`{{${field.key}}}`)}
                                  className={darkMode ? 'hover:bg-[#2A2A2A]' : ''}
                                >
                                  <Copy className="w-4 h-4" strokeWidth={1.5} />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => removeField(index)}
                                  className={`text-red-500 hover:text-red-600 ${darkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'}`}
                                >
                                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>
      
      {/* Add Field Dialog */}
      <Dialog open={showFieldDialog} onOpenChange={setShowFieldDialog}>
        <DialogContent className={darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}>
          <DialogHeader>
            <DialogTitle className={textClass}>Add Custom Field</DialogTitle>
            <DialogDescription>
              Create a field that will be filled in when generating documents
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className={textClass}>Field Label *</Label>
              <Input
                value={newField.label}
                onChange={(e) => setNewField(prev => ({ 
                  ...prev, 
                  label: e.target.value,
                  key: e.target.value.toLowerCase().replace(/\s+/g, '_')
                }))}
                placeholder="e.g., Design Fee"
                className={inputClass}
              />
            </div>
            
            <div>
              <Label className={textClass}>Field Key</Label>
              <Input
                value={newField.key}
                onChange={(e) => setNewField(prev => ({ ...prev, key: e.target.value }))}
                placeholder="e.g., design_fee"
                className={`font-mono ${inputClass}`}
              />
              <p className={`text-xs mt-1 ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
                Use in content as: <span className="font-mono text-[#BC5D41]">{`{{${newField.key || 'field_key'}}}`}</span>
              </p>
            </div>
            
            <div>
              <Label className={textClass}>Field Type</Label>
              <Select 
                value={newField.type} 
                onValueChange={(value) => setNewField(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger className={inputClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                  {FIELD_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value} className={darkMode ? 'text-[#E5E2DD] focus:bg-[#2A2A2A]' : ''}>
                      <span className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" strokeWidth={1.5} />
                        {type.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className={textClass}>Placeholder Text</Label>
              <Input
                value={newField.placeholder}
                onChange={(e) => setNewField(prev => ({ ...prev, placeholder: e.target.value }))}
                placeholder="Hint text shown in empty field"
                className={inputClass}
              />
            </div>
            
            <div>
              <Label className={textClass}>Default Value</Label>
              <Input
                value={newField.default_value}
                onChange={(e) => setNewField(prev => ({ ...prev, default_value: e.target.value }))}
                placeholder="Pre-filled value (optional)"
                className={inputClass}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={newField.required}
                onCheckedChange={(checked) => setNewField(prev => ({ ...prev, required: checked }))}
              />
              <Label className={textClass}>Required field</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFieldDialog(false)} className={darkMode ? 'border-[#2A2A2A]' : ''}>
              Cancel
            </Button>
            <Button onClick={addField} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
              Add Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className={`max-w-3xl max-h-[80vh] overflow-y-auto ${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : ''}`}>
          <DialogHeader>
            <DialogTitle className={textClass}>Document Preview</DialogTitle>
            <DialogDescription>
              Preview with sample data. Placeholders are shown with example values.
            </DialogDescription>
          </DialogHeader>
          
          <div className={`p-6 rounded-lg whitespace-pre-wrap font-mono text-sm ${
            darkMode ? 'bg-[#1A1A1A] text-[#E5E2DD]' : 'bg-gray-50'
          }`}>
            {generatePreview() || 'No content to preview'}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)} className={darkMode ? 'border-[#2A2A2A]' : ''}>
              Close
            </Button>
            <Button className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
              <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Export PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
